/**
 * Channel List Component
 * List of all channels with create button
 * Supports drag-and-drop reordering for admin/super_admin users
 */

import { useState } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { useChannels, useUpdateChannelPositions } from '@/hooks/useChannels';
import { useAuthStore } from '@/store/auth.store';
import ChannelItem from './ChannelItem';
import CreateChannelModal from './CreateChannelModal';
import type { Channel } from '@/types/api.types';

interface ChannelListProps {
  hideHeader?: boolean;
}

const ChannelList = ({ hideHeader = false }: ChannelListProps) => {
  const { data: channels, isLoading, error } = useChannels();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const { user } = useAuthStore();
  const updatePositions = useUpdateChannelPositions();

  const canReorder = user?.role === 'admin' || user?.role === 'super_admin';

  const activeChannels = channels?.filter((ch) => !ch.is_archived) || [];
  const archivedChannels = channels?.filter((ch) => ch.is_archived) || [];

  // Local ordered state for optimistic DnD reorder (adjust state during render on change)
  const [orderedChannels, setOrderedChannels] = useState<Channel[]>(activeChannels);
  const [syncedChannels, setSyncedChannels] = useState(channels);
  if (channels !== syncedChannels) {
    setSyncedChannels(channels);
    setOrderedChannels(activeChannels);
  }

  // DnD sensors — require 5px drag distance to prevent accidental drags
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor)
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = orderedChannels.findIndex((c) => c.id === active.id);
    const newIndex = orderedChannels.findIndex((c) => c.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const newOrder = arrayMove(orderedChannels, oldIndex, newIndex);
    setOrderedChannels(newOrder); // Optimistic update

    const positions = newOrder.map((ch, i) => ({ id: ch.id, position: i }));
    updatePositions.mutate(positions, {
      onError: () => setOrderedChannels(activeChannels), // Rollback
    });
  };

  if (isLoading) {
    return (
      <div className="p-4 space-y-2">
        {[1, 2, 3, 4, 5].map((i) => (
          <div
            key={i}
            className="h-8 bg-gray-800 rounded-lg animate-pulse"
          />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="text-sm text-red-400">
          Грешка при зареждане на каналите
        </div>
      </div>
    );
  }

  const displayChannels = canReorder ? orderedChannels : activeChannels;

  return (
    <div className="flex flex-col h-full">
      {/* Header with Create Button - only show if not hidden */}
      {!hideHeader && (
        <div className="px-4 py-2 flex items-center justify-between">
          <div className="text-xs font-semibold text-gray-400 dark:text-dark-text-muted uppercase">
            Канали
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="p-1 text-gray-400 dark:text-dark-text-muted hover:text-white hover:bg-gray-800 dark:hover:bg-dark-bg-tertiary rounded transition-colors"
            title="Създай нов канал"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
          </button>
        </div>
      )}

      {/* Active Channels */}
      <div className={`flex-1 overflow-y-auto ${hideHeader ? 'px-2' : 'px-4'} py-2 space-y-0.5`}>
        {displayChannels.length === 0 ? (
          <div className="text-sm text-gray-500 dark:text-dark-text-muted text-center py-4">
            Няма канали
          </div>
        ) : canReorder ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={displayChannels.map((ch) => ch.id)}
              strategy={verticalListSortingStrategy}
            >
              {displayChannels.map((channel) => (
                <ChannelItem key={channel.id} channel={channel} draggable />
              ))}
            </SortableContext>
          </DndContext>
        ) : (
          displayChannels.map((channel) => (
            <ChannelItem key={channel.id} channel={channel} />
          ))
        )}

        {/* Archived Channels Section */}
        {archivedChannels.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-800">
            <div className="text-xs font-semibold text-gray-500 dark:text-dark-text-muted uppercase mb-2 px-2">
              Архивирани
            </div>
            {archivedChannels.map((channel) => (
              <ChannelItem key={channel.id} channel={channel} />
            ))}
          </div>
        )}
      </div>

      {/* Create Channel Modal */}
      {showCreateModal && (
        <CreateChannelModal onClose={() => setShowCreateModal(false)} />
      )}
    </div>
  );
};

export default ChannelList;
